/**
 * `/learn/<id>` — one lesson, public, and readable without an account (master plan §8).
 *
 * Everything on it comes from the lesson file through the build (`scripts/build-learn.ts`):
 * the words as blocks, each range as the chart node the trainer deals from — drawn by the
 * trainer's own `RangeGrid`, uncovered — the checkpoint, and "Try it" pointing at the
 * card the header names. The HTML inside a block was escaped at build time from a file in
 * this repository; nothing a player or a server types reaches it.
 *
 * THE CHECKPOINT IS A READING AID. Three questions, answered by tapping; the answer and
 * the why appear, and nothing is sent anywhere or counted (decision 6: it does not touch
 * Trainer Rating). A `free: false` lesson's words are not in the bundle at all, so its
 * page is the title, the promise and the lock.
 *
 * SEO: a crawler that runs the page gets the title, description and canonical set here;
 * one that does not gets them from `learn/<id>.html`, which `tools/learn-pages.mjs`
 * writes into the build with the same three filled in, so a shared link has a card.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { RangeGrid } from "../components/RangeGrid";
import type { ChartNode } from "../lib/api";
import { useMeContext } from "../lib/MeContext";
import { LESSONS, lessonById, type Block, type Checkpoint, type Lesson } from "./library";
import "./learn.css";

const SITE = "https://bluffbusters.net";
/* The site's own head, from `index.html`. Leaving a lesson puts THESE back, not what was
   there on arrival — a visitor who landed on a lesson's static page arrived with that
   lesson's description and canonical already set, and would carry them to every page. */
const SITE_DESCRIPTION = "Solved push/fold and ICM charts computed by our own engine, and a table of poker bots to practise against. Every decision priced, every claim sourced.";
const SITE_CANONICAL = `${SITE}/`;
const SITE_TITLE = "BluffBusters — short-stack tournament poker training";

/** Set the title, description and canonical for a lesson; the cleanup restores the site's. */
function setHead(lesson: Lesson): () => void {
  document.title = `${lesson.title} — Learn — BluffBusters`;
  const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (meta) meta.content = lesson.promise;
  const canon = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (canon) canon.href = `${SITE}/learn/${lesson.id}`;
  return () => {
    /* The site's, like the other two: what was there on arrival may be another lesson's
       (a static lesson page), and `useRouteTitle` steps aside under `/learn/`. */
    document.title = SITE_TITLE;
    if (meta) meta.content = SITE_DESCRIPTION;
    if (canon) canon.href = SITE_CANONICAL;
  };
}

export function LessonPage() {
  const { id = "" } = useParams();
  const lesson = lessonById(id);
  const { me } = useMeContext();
  const pro = me?.plan.id === "pro";

  useEffect(() => (lesson ? setHead(lesson) : undefined), [lesson]);
  /* An id that is not in the library answers 200 (the app is one page), so it asks not
     to be indexed — a crawler following a stale link must not file "No lesson here". */
  useEffect(() => {
    if (lesson) return undefined;
    document.title = "No lesson here — Learn — BluffBusters";
    const robots = document.createElement("meta");
    robots.name = "robots"; robots.content = "noindex";
    document.head.append(robots);
    return () => { robots.remove(); document.title = SITE_TITLE; };
  }, [lesson]);
  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  if (!lesson) {
    return (
      <div className="page prose learn-page">
        <h1>No lesson here</h1>
        <p className="muted">That page is not in the library. <Link to="/poker/learn">The Learn shelves</Link> list every one.</p>
      </div>
    );
  }

  const same = LESSONS.filter((l) => l.shelf === lesson.shelf);
  const at = same.findIndex((l) => l.id === lesson.id);
  const prev = at > 0 ? same[at - 1] : undefined;
  const next = at >= 0 && at < same.length - 1 ? same[at + 1] : undefined;

  return (
    <article className="page learn-page" data-shelf={lesson.shelf}>
      <p className="learn-back"><Link to="/poker/learn">‹ Learn</Link></p>
      {lesson.sample && (
        <p className="learn-sample-banner" role="note">
          Sample — a plumbing fixture for the Learn build, not lesson copy. It is in the
          harness only and never ships.
        </p>
      )}
      <header className="learn-head">
        <p className="learn-kicker">
          {lesson.shelf === "primer" ? "The Primer" : `The Study · ${lesson.trackLabel}`}
        </p>
        <h1>{lesson.title}</h1>
        <p className="lede">{lesson.promise}</p>
        <p className="learn-byline">
          {lesson.narratorName} · <span className="num">{lesson.readingMinutes}</span> min read
          {!lesson.free && <> · <em className="learn-pro">Poker Pro</em></>}
        </p>
      </header>

      {lesson.free && lesson.blocks ? (
        <div className="learn-text">
          {lesson.blocks.map((b, i) => <BlockView key={i} b={b} lesson={lesson} />)}
        </div>
      ) : (
        <section className="learn-locked" data-td="lesson-lock">
          <h2>Part of Poker Pro</h2>
          {pro ? (
            <p>
              Included in your plan. The Pro lessons open on this page from the next release;
              the title and the promise above are what it covers.
            </p>
          ) : (
            <>
              <p>
                The Primer and most of the Study are free and always will be. This lesson is
                one of the few that is part of Poker Pro, with Your Syllabus and the full leak report.
              </p>
              <Link className="btn" to="/pricing">See Poker Pro</Link>
            </>
          )}
        </section>
      )}

      {lesson.free && lesson.checkpoint && lesson.checkpoint.questions.length > 0 && (
        /* Keyed by lesson: the pager reuses this page, and a checkpoint answered on one
           lesson must not arrive answered on the next. */
        <CheckpointView key={lesson.id} cp={lesson.checkpoint} />
      )}

      <nav className="learn-pager" aria-label="More lessons">
        {prev ? <Link to={`/learn/${prev.id}`} className="learn-prev">‹ {prev.title}</Link> : <span />}
        {next ? <Link to={`/learn/${next.id}`} className="learn-next">{next.title} ›</Link> : <span />}
      </nav>
    </article>
  );
}

function BlockView({ b, lesson }: { b: Block; lesson: Lesson }) {
  switch (b.t) {
    case "h2": return <h2>{b.text}</h2>;
    case "h3": return <h3>{b.text}</h3>;
    case "p": return <p dangerouslySetInnerHTML={{ __html: b.html }} />;
    case "quote": return <blockquote dangerouslySetInnerHTML={{ __html: b.html }} />;
    case "ul": return <ul>{b.items.map((h, i) => <li key={i} dangerouslySetInnerHTML={{ __html: h }} />)}</ul>;
    case "ol": return <ol>{b.items.map((h, i) => <li key={i} dangerouslySetInnerHTML={{ __html: h }} />)}</ol>;
    case "range":
      return (
        <figure className="learn-range" data-td="range" data-chart={b.chartKey}>
          {/* The trainer's own grid, uncovered: the page is not a drill. */}
          <RangeGrid node={b.node as unknown as ChartNode} dealtCode={null} revealed />
          <figcaption>
            <b>{b.label}</b>
            <span className="muted small">From the chart the trainer deals from — <code>{b.chartKey}</code>.</span>
          </figcaption>
        </figure>
      );
    case "try":
      return (
        <aside className="learn-try">
          <p dangerouslySetInnerHTML={{ __html: b.html }} />
          <Link className="btn" to={lesson.card.href}>{lesson.card.title}</Link>
        </aside>
      );
  }
}

/** Three questions, answered by a tap. Nothing leaves this component. */
function CheckpointView({ cp }: { cp: Checkpoint }) {
  const [picked, setPicked] = useState<Record<number, number>>({});
  return (
    <section className="learn-checkpoint" aria-labelledby="learn-cp">
      <h2 id="learn-cp">Checkpoint</h2>
      <p className="muted small">A reading aid. Nothing here is scored or saved.</p>
      <ol>
        {cp.questions.map((q, qi) => {
          const chose = picked[qi];
          const answered = chose !== undefined;
          return (
            <li key={qi} className="learn-q">
              <p dangerouslySetInnerHTML={{ __html: q.q }} />
              <div className="learn-options" role="group">
                {q.options.map((o, oi) => (
                  <button key={oi} type="button" disabled={answered}
                          className={answered ? (o.correct ? "is-right" : oi === chose ? "is-wrong" : "") : ""}
                          onClick={() => setPicked((p) => ({ ...p, [qi]: oi }))}
                          dangerouslySetInnerHTML={{ __html: o.html }} />
                ))}
              </div>
              {answered && (
                <p className="learn-why" role="status">
                  <b>{q.options[chose]!.correct ? "Yes." : "Not quite."}</b>{" "}
                  {q.why && <span dangerouslySetInnerHTML={{ __html: q.why }} />}
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
